import React, { useState, useRef, useEffect } from "react";
import { Input, Button, message, Modal } from "antd";
import {
  SendOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  CopyOutlined,
  CheckOutlined,
  PlusOutlined,
  MessageOutlined,
  BulbOutlined,
  UpOutlined,
  DownOutlined,
  MenuOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import moment from "moment";
import "./Chats.css";

const { TextArea } = Input;

const Chats = () => {
  const userId = useSelector((state) => state.user._id);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Store tools per message ID: { [messageId]: { activeTools: [], completedTools: [] } }
  const [messageTools, setMessageTools] = useState({});
  // Track which tool timelines are expanded per message: { [messageId]: boolean }
  const [expandedTimelines, setExpandedTimelines] = useState({});
  const [chatId, setChatId] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  // Get current month and year
  const currentDate = new Date();
  const defaultMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
  const defaultYear = currentDate.getFullYear();

  // Auto scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll when assistant message content changes (for streaming)
  useEffect(() => {
    const assistantMessage = messages.find(
      (msg) => msg.role === "assistant" && msg.content
    );
    if (assistantMessage && isLoading) {
      scrollToBottom();
    }
  }, [messages, isLoading]);

  // Scroll when any message gets tools
  useEffect(() => {
    const hasActiveTools = Object.values(messageTools).some(
      (tools) => tools.activeTools && tools.activeTools.length > 0
    );
    if (hasActiveTools) {
      scrollToBottom();
    }
  }, [messageTools]);

  // Ensure tools persist after chat completes
  // Tools should NOT be cleared when chat completes, only when a new message is sent

  // Function to create a new chat
  const createChat = async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/aiReportingInsights/chat/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            defaultMonth: defaultMonth,
            defaultYear: defaultYear,
            userId: userId || "user_123",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newChatId = data.chatId;

      if (newChatId) {
        setChatId(newChatId);
        console.log("Chat created:", newChatId);
      } else {
        console.warn("No chatId in response:", data);
      }
      return newChatId;
    } catch (error) {
      console.error("Error creating chat:", error);
      message.error("Failed to create chat. Please try again.");
      return null;
    }
  };

  // Fetch chat history
  const fetchChatHistory = async () => {
    if (!userId) return;

    setIsHistoryLoading(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/aiReportingInsights/chat/history/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data && data.chats) {
        setChatHistory(data.chats);
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
      message.error("Failed to load chat history.");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // Load a specific chat
  const loadChat = async (selectedChatId) => {
    if (!selectedChatId || selectedChatId === chatId) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/aiReportingInsights/chat/messages/${selectedChatId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && data.messages && Array.isArray(data.messages)) {
        // Transform API messages to our format
        const transformedMessages = [];
        const toolsMap = {};

        data.messages.forEach((msg, index) => {
          const messageId = msg.messageId || Date.now() + index;
          const transformedMessage = {
            id: messageId,
            role: msg.role || "user",
            content: msg.content || "",
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          };
          transformedMessages.push(transformedMessage);

          // Handle tools for assistant messages
          if (
            msg.role === "assistant" &&
            msg.toolsCalled &&
            Array.isArray(msg.toolsCalled) &&
            msg.toolsCalled.length > 0
          ) {
            // Find the previous user message to associate tools with
            const userMessageIndex = transformedMessages.length - 2;
            if (
              userMessageIndex >= 0 &&
              transformedMessages[userMessageIndex].role === "user"
            ) {
              const userMessageId = transformedMessages[userMessageIndex].id;

              // Transform tools to our format
              const completedTools = msg.toolsCalled.map((tool) => ({
                tool_name: tool.toolName,
                display_name: tool.displayName || tool.toolName,
                description: tool.arguments
                  ? JSON.stringify(tool.arguments)
                  : "",
                icon: tool.icon || "🔧",
                status: tool.status || "success",
                tool_index: msg.toolsCalled.indexOf(tool),
                total_tools: msg.toolsCalled.length,
              }));

              toolsMap[userMessageId] = {
                activeTools: [],
                completedTools: completedTools,
                aiMessageId: messageId,
              };
            }
          }
        });

        setMessages(transformedMessages);
        setChatId(data.chatId || selectedChatId);
        setMessageTools(toolsMap);
        setExpandedTimelines({});
      } else {
        // If no messages, just set the chatId and clear messages
        setMessages([]);
        setChatId(selectedChatId);
        setMessageTools({});
        setExpandedTimelines({});
      }
    } catch (error) {
      console.error("Error loading chat:", error);
      message.error("Failed to load chat. Please try again.");
      setMessages([]);
      setChatId(selectedChatId);
      setMessageTools({});
      setExpandedTimelines({});
    } finally {
      setIsLoading(false);
      setIsHistoryOpen(false);
    }
  };

  // Delete a specific chat
  const deleteChat = async (chatIdToDelete, e) => {
    // Stop event propagation to prevent loading the chat when clicking delete
    if (e) {
      e.stopPropagation();
    }

    // Show Ant Design confirmation modal
    Modal.confirm({
      title: "Delete Chat",
      content: "Are you sure you want to delete this chat? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await fetch(
            `${apiBaseUrl}/aiReportingInsights/chat/${chatIdToDelete}`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          // If the deleted chat is the currently active chat, clear it
          if (chatIdToDelete === chatId) {
            setMessages([]);
            setChatId(null);
            setMessageTools({});
            setExpandedTimelines({});
            setInputValue("");
            setCopiedMessageId(null);
            // Create a new chat
            await createChat();
          }

          // Refresh chat history
          await fetchChatHistory();
          message.success("Chat deleted successfully");
        } catch (error) {
          console.error("Error deleting chat:", error);
          message.error("Failed to delete chat. Please try again.");
        }
      },
    });
  };

  // Create chat when component first mounts
  useEffect(() => {
    if (!chatId) {
      createChat();
    }
    // Fetch chat history on mount
    fetchChatHistory();
  }, []); // Empty dependency array - only run on mount

  const handleSend = async (messageText = null) => {
    const messageToSend = messageText || inputValue.trim();

    if (!messageToSend || isLoading) {
      return;
    }

    const userMessage = messageToSend;
    if (!messageText) {
      setInputValue("");
    }

    // Don't clear previous message tools - they persist per message
    // New message will get its own tools initialized when AI message is created

    // Add user message to chat
    const userMessageId = Date.now();
    const newUserMessage = {
      id: userMessageId,
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newUserMessage]);

    // Add placeholder for AI response
    const aiMessageId = Date.now() + 1;
    const newAiMessage = {
      id: aiMessageId,
      role: "assistant",
      content: "",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newAiMessage]);

    // Initialize tools for this user message (tools will appear after user message, before assistant response)
    // We use userMessageId as the key so tools appear in the right place
    setMessageTools((prev) => ({
      ...prev,
      [userMessageId]: {
        activeTools: [],
        completedTools: [],
        aiMessageId: aiMessageId, // Store reference to the AI message for tool events
      },
    }));
    // Timeline is always expanded, no need to initialize state

    setIsLoading(true);

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        `${apiBaseUrl}/aiReportingInsights/chat/stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            defaultMonth: defaultMonth,
            defaultYear: defaultYear,
            message: userMessage,
            userId: userId || "user_123",
            ...(chatId && { chatId: chatId }),
          }),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let accumulatedContent = "";
      let isStreaming = true;

      // Function to update message content immediately
      const updateMessageContent = (content) => {
        if (!isStreaming) return;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === aiMessageId ? { ...msg, content: content } : msg
          )
        );
      };

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode the chunk and add to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process buffer line by line (SSE format: "data: {...}")
          const lines = buffer.split("\n");
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (!line.trim()) continue;

            // Parse SSE format: "data: {...}"
            if (line.startsWith("data: ")) {
              try {
                const jsonStr = line.substring(6); // Remove "data: " prefix
                const eventData = JSON.parse(jsonStr);

                // Handle different event types
                switch (eventData.type) {
                  case "session_created":
                    // Keep this for backward compatibility, but prefer chatId from create endpoint
                    const newSessionId = eventData.data?.session_id;
                    if (newSessionId && !chatId) {
                      setChatId(newSessionId);
                      console.log("Session created:", newSessionId);
                    }
                    break;

                  case "status":
                    const statusMessage = eventData.data?.message || "";
                    console.log(
                      "Status:",
                      eventData.data?.status,
                      statusMessage
                    );
                    // Optionally show status in UI
                    break;

                  case "tool_started":
                    const toolData = eventData.data;
                    console.log("Tool started event received:", toolData);
                    if (toolData) {
                      // Convert escaped Unicode to emoji
                      // The icon might come as escaped unicode string or already decoded
                      let icon = toolData.icon || "🔧"; // default

                      if (icon && typeof icon === "string") {
                        try {
                          // Check if it's an escaped unicode string (like "\\ud83d\\uddfa\\ufe0f")
                          if (icon.includes("\\u")) {
                            // Handle escaped unicode - decode it
                            icon = icon.replace(
                              /\\u([0-9a-fA-F]{4})/g,
                              (match, code) =>
                                String.fromCharCode(parseInt(code, 16))
                            );
                          }
                          // If icon is empty after parsing, use default
                          if (!icon || icon.trim() === "") {
                            icon = "🔧";
                          }
                          console.log(
                            "Parsed icon:",
                            icon,
                            "from:",
                            toolData.icon
                          );
                        } catch (e) {
                          console.warn(
                            "Failed to parse icon:",
                            toolData.icon,
                            e
                          );
                          icon = "🔧";
                        }
                      } else {
                        icon = "🔧";
                      }

                      // Map camelCase API response to internal snake_case structure
                      const toolName = toolData.toolName || toolData.tool_name;
                      const displayName =
                        toolData.displayName ||
                        toolData.display_name ||
                        toolName;
                      const toolIndex =
                        toolData.toolIndex !== undefined
                          ? toolData.toolIndex
                          : toolData.tool_index;
                      const totalTools =
                        toolData.totalTools !== undefined
                          ? toolData.totalTools
                          : toolData.total_tools;

                      const newTool = {
                        tool_name: toolName,
                        display_name: displayName,
                        description: toolData.description || "",
                        icon: icon,
                        tool_index: toolIndex,
                        total_tools: totalTools,
                      };

                      // Use the userMessageId from closure (tools belong to the user message that triggered this response)
                      console.log(
                        "Adding tool to active tools for user message:",
                        userMessageId,
                        newTool
                      );
                      setMessageTools((prev) => {
                        const currentTools = prev[userMessageId] || {
                          activeTools: [],
                          completedTools: [],
                        };
                        // Check if tool already exists to avoid duplicates
                        const exists = currentTools.activeTools.some(
                          (t) => t.tool_name === newTool.tool_name
                        );
                        if (exists) {
                          console.log("Tool already exists, skipping");
                          return prev;
                        }
                        const updated = {
                          ...prev,
                          [userMessageId]: {
                            ...currentTools,
                            activeTools: [...currentTools.activeTools, newTool],
                          },
                        };
                        console.log("Message tools after update:", updated);
                        return updated;
                      });
                      // Timeline is always expanded, no need to set state
                    }
                    break;

                  case "tool_ended":
                    const endedToolData = eventData.data;
                    // Support both camelCase and snake_case for backward compatibility
                    const endedToolName =
                      endedToolData?.toolName || endedToolData?.tool_name;
                    console.log("Tool ended event received:", endedToolName);
                    if (endedToolName) {
                      // Use the userMessageId from closure (tools belong to the user message that triggered this response)
                      setMessageTools((prev) => {
                        const currentTools = prev[userMessageId] || {
                          activeTools: [],
                          completedTools: [],
                        };
                        const toolToComplete = currentTools.activeTools.find(
                          (tool) => tool.tool_name === endedToolName
                        );
                        if (toolToComplete) {
                          // Move to completed tools
                          const updated = {
                            ...prev,
                            [userMessageId]: {
                              ...currentTools,
                              activeTools: currentTools.activeTools.filter(
                                (tool) => tool.tool_name !== endedToolName
                              ),
                              completedTools: [
                                {
                                  ...toolToComplete,
                                  status: endedToolData?.status || "success",
                                  endedAt: new Date(),
                                },
                                ...currentTools.completedTools, // Add new completed tool at the beginning
                              ],
                            },
                          };
                          console.log(
                            "Message tools after tool ended:",
                            updated
                          );
                          return updated;
                        }
                        return prev;
                      });
                    }
                    break;

                  case "content":
                    // Accumulate content chunks and update immediately
                    const contentChunk = eventData.data || "";
                    accumulatedContent += contentChunk;
                    // Update UI immediately with accumulated content
                    updateMessageContent(accumulatedContent);
                    break;

                  case "error":
                    const errorMessage = eventData.data || "An error occurred";
                    message.error(errorMessage);
                    throw new Error(errorMessage);

                  default:
                    console.log("Unknown event type:", eventData.type);
                }
              } catch (parseError) {
                // If JSON parsing fails, it might be a partial chunk
                console.warn("Failed to parse SSE line:", line, parseError);
              }
            }
          }
        }

        // Process remaining buffer if it contains complete SSE data
        if (buffer.trim() && buffer.startsWith("data: ")) {
          try {
            const jsonStr = buffer.substring(6);
            const eventData = JSON.parse(jsonStr);
            if (eventData.type === "content") {
              accumulatedContent += eventData.data || "";
              updateMessageContent(accumulatedContent);
            }
          } catch (e) {
            // Partial buffer, will be processed in next chunk
            console.warn("Partial buffer:", buffer);
          }
        }
      } finally {
        isStreaming = false;
        reader.releaseLock();
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Request aborted");
        return;
      }
      console.error("Error sending message:", error);
      message.error("Failed to get response. Please try again.");

      // Remove the failed AI message
      setMessages((prev) => prev.filter((msg) => msg.id !== aiMessageId));
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      // Ensure tools persist after chat completes - don't clear them here
      // Tools should only be cleared when starting a new message
      console.log("Response completed. Current messageTools:", messageTools);
      console.log("Current expandedTimelines:", expandedTimelines);
      // Refresh chat history after message is sent
      fetchChatHistory();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Convert markdown to plain text (as displayed in chat)
  const markdownToPlainText = (markdown) => {
    if (!markdown) return "";

    let text = markdown;

    // Remove headers (###, ##, #) - keep the text
    text = text.replace(/^#{1,6}\s+(.+)$/gm, "$1");

    // Remove bold (**text** or __text__) - keep the text
    text = text.replace(/\*\*(.+?)\*\*/g, "$1");
    text = text.replace(/__(.+?)__/g, "$1");

    // Remove italic (*text* or _text_) - simple approach
    // First handle cases where * is not a list marker
    text = text.replace(/\*([^*\n]+?)\*/g, "$1");
    text = text.replace(/_([^_\n]+?)_/g, "$1");

    // Remove links [text](url) -> text
    text = text.replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1");

    // Remove inline code `code` - keep the code text
    text = text.replace(/`([^`]+)`/g, "$1");

    // Remove code blocks
    text = text.replace(/```[\s\S]*?```/g, "");

    // Handle numbered lists - keep the number format
    // The numbered lists should already be fine, just ensure spacing

    // Handle bullet lists - convert to simple bullets
    text = text.replace(/^[\s]*[-*+]\s+/gm, "• ");

    // Remove blockquotes
    text = text.replace(/^>\s+/gm, "");

    // Remove horizontal rules
    text = text.replace(/^---$/gm, "");
    text = text.replace(/^\*\*\*$/gm, "");

    // Clean up extra whitespace but preserve list structure
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    return text;
  };

  const handleCopy = async (messageId, content, isAssistant = false) => {
    try {
      // For assistant messages, convert markdown to plain text
      // For user messages, copy as-is
      const textToCopy = isAssistant ? markdownToPlainText(content) : content;

      await navigator.clipboard.writeText(textToCopy);

      setCopiedMessageId(messageId);
      message.success("Copied to clipboard!");

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      message.error("Failed to copy to clipboard");
    }
  };

  const handleNewChat = async () => {
    // Cancel any ongoing request first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear all messages
    setMessages([]);
    // Clear chat ID to start fresh
    setChatId(null);
    // Clear all message tools
    setMessageTools({});
    // Clear all expanded timelines
    setExpandedTimelines({});
    // Clear input
    setInputValue("");
    // Clear copied message state
    setCopiedMessageId(null);
    setIsLoading(true);

    // Create a new chat
    await createChat();
    // Refresh chat history
    await fetchChatHistory();

    setIsLoading(false);
  };

  return (
    <div className="ai-chat-container">
      <div className="ai-chat-layout">
        {/* History Sidebar */}
        <div
          className={`ai-chat-history-sidebar ${isHistoryOpen ? "open" : ""}`}
        >
          <div className="ai-chat-history-header">
            <h3>Your Chats</h3>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsHistoryOpen(false)}
              className="ai-chat-history-close"
            />
          </div>
          <div className="ai-chat-history-content">
            {isHistoryLoading ? (
              <div className="ai-chat-history-loading">
                <LoadingOutlined /> Loading...
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="ai-chat-history-empty">No chat history</div>
            ) : (
              <div className="ai-chat-history-list">
                {chatHistory.map((chat) => (
                  <div
                    key={chat.chatId}
                    className={`ai-chat-history-item ${
                      chat.chatId === chatId ? "active" : ""
                    }`}
                    onClick={() => loadChat(chat.chatId)}
                  >
                    <div className="ai-chat-history-item-content">
                      <div className="ai-chat-history-item-title">
                        {chat.title || "New Chat"}
                      </div>
                      <div className="ai-chat-history-item-meta">
                        {moment(chat.updatedAt).format("DD/MM/YY")}
                      </div>
                    </div>
                    <button
                      className="ai-chat-history-item-delete"
                      onClick={(e) => deleteChat(chat.chatId, e)}
                      title="Delete chat"
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Content */}
        <div className="ai-chat-main-content">
          {/* New Chat Button */}
          <div className="ai-chat-header">
            {!isHistoryOpen && (
              <Button
                type="default"
                icon={<MenuOutlined />}
                onClick={() => setIsHistoryOpen(true)}
                className="ai-chat-history-toggle"
              >
                History
              </Button>
            )}
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={handleNewChat}
              className="ai-chat-new-button"
              disabled={isLoading}
            >
              New Chat
            </Button>
          </div>
          <div className="ai-chat-messages" ref={messagesContainerRef}>
            {messages.length === 0 ? (
              <div className="ai-chat-empty-state">
                <div className="ai-chat-empty-icon">
                  <MessageOutlined />
                </div>
                <h3 className="ai-chat-empty-title">
                  Start a conversation with AI
                </h3>
                <p className="ai-chat-empty-description">
                  Ask questions about your data and get instant insights!
                </p>
                <div className="ai-chat-empty-suggestions">
                  <div
                    className="ai-chat-suggestion-item"
                    onClick={() =>
                      !isLoading &&
                      handleSend("Get insights on inventory performance")
                    }
                    style={{
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.6 : 1,
                    }}
                  >
                    <BulbOutlined className="ai-chat-suggestion-icon" />
                    <span>Get insights on inventory performance</span>
                  </div>
                  <div
                    className="ai-chat-suggestion-item"
                    onClick={() =>
                      !isLoading && handleSend("Analyze revenue trends by zone")
                    }
                    style={{
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.6 : 1,
                    }}
                  >
                    <BulbOutlined className="ai-chat-suggestion-icon" />
                    <span>Analyze revenue trends by zone</span>
                  </div>
                  <div
                    className="ai-chat-suggestion-item"
                    onClick={() =>
                      !isLoading &&
                      handleSend("Month over month employee performance")
                    }
                    style={{
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.6 : 1,
                    }}
                  >
                    <BulbOutlined className="ai-chat-suggestion-icon" />
                    <span>Month over month employee performance</span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, index) => {
                  const isUserMessage = msg.role === "user";
                  const isAssistantMessage = msg.role === "assistant";
                  // Get tools for this user message (tools appear after user message, before assistant response)
                  const toolsForMessage = isUserMessage
                    ? messageTools[msg.id] || {
                        activeTools: [],
                        completedTools: [],
                      }
                    : { activeTools: [], completedTools: [] };
                  const hasTools =
                    toolsForMessage.activeTools.length > 0 ||
                    toolsForMessage.completedTools.length > 0;
                  // Show tools dropdown after user message if it has tools
                  const showToolStatusAfterThis = isUserMessage && hasTools;
                  const isTimelineExpanded = true; // Always expanded

                  // Find the corresponding assistant message to check if response is complete
                  const assistantMessageIndex = messages.findIndex(
                    (m, idx) => idx > index && m.role === "assistant"
                  );
                  const assistantMessage =
                    assistantMessageIndex >= 0
                      ? messages[assistantMessageIndex]
                      : null;

                  // Find the message currently being processed (last empty assistant message when isLoading is true)
                  const currentlyProcessingMessageId = isLoading
                    ? (() => {
                        // Find the last assistant message that is empty or has minimal content
                        for (let i = messages.length - 1; i >= 0; i--) {
                          if (
                            messages[i].role === "assistant" &&
                            (!messages[i].content ||
                              messages[i].content.trim().length === 0)
                          ) {
                            return messages[i].id;
                          }
                        }
                        return null;
                      })()
                    : null;

                  // Check if this user message's assistant message is the one currently being processed
                  const isCurrentlyProcessing =
                    assistantMessage &&
                    currentlyProcessingMessageId === assistantMessage.id;

                  // Response is complete if assistant message exists and has content
                  // (don't check isLoading - a message with content is complete regardless of global loading state)
                  const isResponseComplete =
                    assistantMessage &&
                    assistantMessage.content &&
                    assistantMessage.content.trim().length > 0;

                  // Show "Thinking..." only if:
                  // 1. This is the message currently being processed AND has active tools, OR
                  // 2. This is the message currently being processed AND has tools but response is not complete yet
                  const shouldShowThinking =
                    isCurrentlyProcessing &&
                    (toolsForMessage.activeTools.length > 0 ||
                      (hasTools && !isResponseComplete));
                  // Check if tools container is expanded (default to true when thinking, default to false when completed, allow toggle when completed)
                  const isToolsExpanded =
                    expandedTimelines[msg.id] !== undefined
                      ? expandedTimelines[msg.id]
                      : shouldShowThinking; // Default to expanded only when thinking, closed when completed

                  // Debug: Log tools state for user messages with tools
                  if (isUserMessage && hasTools) {
                    console.log(`[RENDER] Message ${msg.id} - Tools:`, {
                      toolsForMessage,
                      hasTools,
                      showToolStatusAfterThis,
                      isTimelineExpanded,
                      expandedState: expandedTimelines[msg.id],
                    });
                  }

                  return (
                    <React.Fragment key={msg.id}>
                      <div
                        className={`ai-chat-message ai-chat-message-${msg.role}`}
                      >
                        <div className="ai-chat-message-content">
                          {msg.role === "assistant" &&
                          msg.content === "" &&
                          isLoading ? (
                            <div className="ai-chat-typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          ) : msg.role === "assistant" ? (
                            <div className="ai-chat-markdown-wrapper">
                              <div className="ai-chat-markdown">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {msg.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          ) : (
                            <div className="ai-chat-text">{msg.content}</div>
                          )}
                          {/* Copy button - show on hover */}
                          {msg.content &&
                            !(
                              msg.role === "assistant" &&
                              msg.content === "" &&
                              isLoading
                            ) && (
                              <button
                                className="ai-chat-copy-button"
                                onClick={() =>
                                  handleCopy(
                                    msg.id,
                                    msg.content,
                                    msg.role === "assistant"
                                  )
                                }
                                title="Copy message"
                              >
                                {copiedMessageId === msg.id ? (
                                  <CheckOutlined className="ai-chat-copy-icon" />
                                ) : (
                                  <CopyOutlined className="ai-chat-copy-icon" />
                                )}
                              </button>
                            )}
                        </div>
                      </div>
                      {/* Show tools after user message (before assistant response) if it has tools */}
                      {showToolStatusAfterThis && (
                        <div
                          className="ai-chat-tools-simple"
                          key={`tools-${msg.id}`}
                        >
                          {/* Header: Show "Thinking..." with animated dots when active, "Tools Used (X)" when response is complete */}
                          <div
                            className="ai-chat-tools-header"
                            onClick={() => {
                              // Only allow toggle when showing "Tools Used" (response is complete)
                              if (
                                isResponseComplete &&
                                toolsForMessage.completedTools.length > 0
                              ) {
                                setExpandedTimelines((prev) => ({
                                  ...prev,
                                  [msg.id]: !isToolsExpanded,
                                }));
                              }
                            }}
                            style={{
                              cursor:
                                isResponseComplete &&
                                toolsForMessage.completedTools.length > 0
                                  ? "pointer"
                                  : "default",
                            }}
                          >
                            <div className="ai-chat-tools-header-content">
                              {shouldShowThinking ? (
                                <span className="ai-chat-tools-thinking">
                                  Thinking
                                  <span className="ai-chat-thinking-dots">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                  </span>
                                </span>
                              ) : isResponseComplete &&
                                toolsForMessage.completedTools.length > 0 ? (
                                <span className="ai-chat-tools-used">
                                  Tools Used (
                                  {toolsForMessage.completedTools.length})
                                </span>
                              ) : (
                                <span className="ai-chat-tools-default">
                                  Tools
                                </span>
                              )}
                            </div>
                            {/* Show arrow only when showing "Tools Used" (response is complete) */}
                            {isResponseComplete &&
                              toolsForMessage.completedTools.length > 0 && (
                                <span className="ai-chat-tools-arrow">
                                  {isToolsExpanded ? (
                                    <UpOutlined />
                                  ) : (
                                    <DownOutlined />
                                  )}
                                </span>
                              )}
                          </div>
                          {/* Show completed tools first, then active tools - only when expanded */}
                          <div
                            style={{
                              display: isToolsExpanded ? "block" : "none",
                            }}
                          >
                            {(() => {
                              const allTools = [
                                ...toolsForMessage.completedTools,
                                ...toolsForMessage.activeTools,
                              ];
                              console.log(
                                `[RENDER TIMELINE] Message ${msg.id}, tools count: ${allTools.length}`,
                                allTools
                              );
                              return allTools.length > 0 ? (
                                [
                                  ...toolsForMessage.completedTools,
                                  ...toolsForMessage.activeTools,
                                ].map((tool, toolIndex) => {
                                  const isActive =
                                    toolsForMessage.activeTools.some(
                                      (t) => t.tool_name === tool.tool_name
                                    );
                                  const isLast =
                                    toolIndex ===
                                    [
                                      ...toolsForMessage.completedTools,
                                      ...toolsForMessage.activeTools,
                                    ].length -
                                      1;

                                  return (
                                    <div
                                      key={`timeline-${tool.tool_name}-${toolIndex}`}
                                      className={`ai-chat-tool-timeline-item ${
                                        isActive ? "active" : "completed"
                                      }`}
                                    >
                                      <div className="ai-chat-tool-timeline-indicator">
                                        <div className="ai-chat-tool-timeline-dot">
                                          {isActive ? (
                                            <div className="ai-chat-tool-timeline-thinking-dots">
                                              <span></span>
                                              <span></span>
                                              <span></span>
                                            </div>
                                          ) : (
                                            <CheckCircleOutlined className="ai-chat-tool-timeline-check" />
                                          )}
                                        </div>
                                        {!isLast && (
                                          <div className="ai-chat-tool-timeline-line"></div>
                                        )}
                                      </div>
                                      <div className="ai-chat-tool-timeline-content">
                                        <div className="ai-chat-tool-timeline-header-row">
                                          <span className="ai-chat-tool-timeline-icon">
                                            {tool.icon || "🔧"}
                                          </span>
                                          <span className="ai-chat-tool-timeline-name">
                                            {tool.display_name}
                                          </span>
                                        </div>
                                        {tool.description && (
                                          <div className="ai-chat-tool-timeline-desc">
                                            {tool.description}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div
                                  style={{
                                    padding: "12px",
                                    color: "#666",
                                    fontSize: "12px",
                                  }}
                                >
                                  No tools available
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-input-container">
            <TextArea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isLoading}
              className="ai-chat-input"
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              loading={isLoading}
              disabled={!inputValue.trim() || isLoading}
              className="ai-chat-send-button"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chats;
